import firebase from 'firebase/app'
import 'firebase/firestore'
import 'firebase/storage'
import 'firebase/functions'
import 'firebase/auth'
import 'firebase/database'
import env from './env'
import * as data from './env.default.json'
const fs = require('fs')

interface NewTestAppParams {
  auth?: {
    uid?: string,
    email?: string
  },
  projectId: string
}

interface FirestoreTestInterface {
  cleanupConnection: () => Promise<void>,
  clearDB: () => Promise<void>,
  initializeTestApp: (params: Partial<NewTestAppParams>) => firebase.firestore.Firestore,
  assertFails: (pr: Promise<any>) => Promise<any>,
  assertSucceeds: (pr: Promise<any> )=> Promise<any>
  loadFirestoreRules: () => Promise<any>
}

export interface FirestoreConnections {
  firebase: firebase.app.App,
  auth: firebase.auth.Auth,
  db: firebase.firestore.Firestore,
  rtdb: firebase.database.Database,
  firestorage: firebase.storage.Storage,
  functions: firebase.functions.Functions,
  projectId: string,
  serverTimestamp: () => firebase.firestore.FieldValue,
  rtdbServerTimestamp: any,   // real-time database = rtdb
  deleteField: firebase.firestore.FieldValue,
  arrayUnionField: (...elements: any[]) => firebase.firestore.FieldValue,
  arrayRemoveField: (...elements: any[]) => firebase.firestore.FieldValue,
  increment: (amount: number) => firebase.firestore.FieldValue,
  test: FirestoreTestInterface
}

const createFirebaseDatabase = (): FirestoreConnections => {
  const app = firebase.initializeApp(env.firebase)
  const projectId = env.firebase.projectId
  const serverTimestamp = firebase.firestore.FieldValue.serverTimestamp
  const rtdbServerTimestamp = firebase.database.ServerValue.TIMESTAMP
  const rtdb = app.database()
  const fb: FirestoreConnections = {
    // @ts-ignore
    firebase,
    auth: app.auth(),
    firestorage: firebase.storage(),
    functions: firebase.functions(),
    db: firebase.firestore(app),
    rtdb,
    projectId,
    serverTimestamp,
    rtdbServerTimestamp,
    deleteField: firebase.firestore.FieldValue.delete(),
    arrayUnionField: firebase.firestore.FieldValue.arrayUnion,
    arrayRemoveField: firebase.firestore.FieldValue.arrayRemove,
    increment: (amount: number) => firebase.firestore.FieldValue.increment(amount),
    test: {
      cleanupConnection: () => { throw new Error('Cleanup connection only implemented for testing env') },
      clearDB: () => { throw new Error('ClearDB only implemented for testing env') },
      initializeTestApp: (_params: Partial<NewTestAppParams>) => { throw new Error('initializeTestApp only implemented for testing env') },
      assertFails: (_pr: Promise<any>) => { throw new Error('assertFails only implemented for testing env') },
      assertSucceeds: (_pr: Promise<any>) => { throw new Error('assertSucceeds only implemented for testing env') },
      loadFirestoreRules: () => { throw new Error('loadFirestoreRules only implemented for testing env') },
    }
  }
  return fb
}

const createTestFirebaseDatabase = (): FirestoreConnections => {
  process.env.FIRESTORE_EMULATOR_HOST = 'localhost:8888'
  process.env.FIREBASE_AUTH_EMULATOR_HOST = 'localhost:9099'
  const projectId = 'cloudcast-test-' + Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5)
  const admin = firebase.initializeApp({ ...data.firebase.dev.firebase, projectId })
  const auth = admin.auth()
  // @ts-ignore
  auth.useEmulator('http://localhost:9099')
  const testFirebase = require('@firebase/testing') as firebase.app.App
  // @ts-ignore
  const serverTimestamp = testFirebase.firestore.FieldValue.serverTimestamp
  // @ts-ignore
  const rtdbServerTimestamp = testFirebase.database.ServerValue.TIMESTAMP

  // @ts-ignore
  const testAdmin = testFirebase.initializeAdminApp({ projectId })
  const db = testAdmin.firestore() as firebase.firestore.Firestore
  const fb: FirestoreConnections = {
    // @ts-ignore
    firebase: testFirebase,
    auth,
    rtdb: admin.database(),
    firestorage: null as any,
    functions: {
      httpsCallable: (_name: string) => jest.fn()
    } as any,
    db,
    projectId,
    serverTimestamp,
    rtdbServerTimestamp,
    // @ts-ignore
    deleteField: testFirebase.firestore.FieldValue.delete(),
    // @ts-ignore
    arrayUnionField: testFirebase.firestore.FieldValue.arrayUnion,
    // @ts-ignore
    arrayRemoveField: testFirebase.firestore.FieldValue.arrayRemove,
    // @ts-ignore
    increment: (amount: number) => testFirebase.firestore.FieldValue.increment(amount),
    test: {
      cleanupConnection: async () => {
        // @ts-ignore
        await Promise.all(testFirebase.apps().map(app => app.delete()))
        await db.terminate()
      },
      clearDB: async () => {
        // @ts-ignore
        await testFirebase.clearFirestoreData({ projectId })
      },
      initializeTestApp: (params: Partial<NewTestAppParams>) => {
        const data: NewTestAppParams = {
          ...params,
          projectId
        }
        // @ts-ignore
        return testFirebase.initializeTestApp(data).firestore()
      },
      assertFails: (pr: Promise<any>) => {
        // @ts-ignore
        return testFirebase.assertFails(pr)
      },
      assertSucceeds: (pr: Promise<any>) => {
        // @ts-ignore
        return testFirebase.assertSucceeds(pr)
      },
      loadFirestoreRules: () => {
          const rules = fs.readFileSync('firestore.rules', 'utf8')
          // @ts-ignore
          return testFirebase.loadFirestoreRules({ projectId, rules })
       },
    }
  }
  return fb
}


const fb = process.env.NODE_ENV === 'test' ? createTestFirebaseDatabase() : createFirebaseDatabase()
export default fb
